import * as Sentry from '@sentry/nextjs';

import { addFBEvent } from './facebook/addFBEvent';
import { addAdEvent } from './google/addAdEvent';
import { addGAEvent } from './google/addGAEvent';
import { addUETEvent } from './microsoft/addUETEvent';
import { addTikTokEvent } from './tiktok/addTikTokEvent';
import { addClarityEvent } from './microsoft/addClarityEvent';

import type { Plan } from '@/fixtures/plans';

// Use an object to map the ad events to the plan types
const AdEvents = {
  Creator: {
    Annual: 'k5fuCKq3m_EBENvO5dUB',
    Monthly: '80vjCKqGifEBENvO5dUB',
  },
  Pro: {
    Annual: 'dREnCNfJ5Y0YENvO5dUB',
    Monthly: 'E0CzCPvY5Y0YENvO5dUB',
  },
  Essentials: {
    Annual: 'Cs8dCPOxsssaENvO5dUB',
    Monthly: 'CNoZCPCxsssaENvO5dUB',
  },
  Business: {
    Annual: 'PeP0CKPdr-wcENvO5dUB',
    Monthly: '7QCUCPrar7EdENvO5dUB',
  },
};

export const trackRegistrationComplete = () => {
  try {
    // GA.
    addGAEvent('signup-email');

    // Ads.
    addAdEvent('Z1XuCNvNqfEBENvO5dUB');

    // Meta.
    addFBEvent('Signup-Free-email');
    // Also track the aggregated free signup event
    addFBEvent('Signup-Free-Conversion');

    // TikTok.
    addTikTokEvent('CompleteRegistrationEmail');

    // UET.
    addUETEvent('event', 'Free_email', {
      event_category: 'Signup',
    });
    addUETEvent('event', 'for_audience', {
      event_category: 'Signup',
    });

    // Clarity.
    addClarityEvent('signup_completed');
  } catch (e) {
    console.warn('Error tracking registration', e);
  }
};

export const trackRegistrationGoogle = () => {
  try {
    // GA.
    addGAEvent('signup-google');

    // Ads.
    addAdEvent('VseNCKfkqfEBENvO5dUB');

    // Meta.
    addFBEvent('Signup-Free-google');
    // Also track the aggregated free signup event
    addFBEvent('Signup-Free-Conversion');

    // TikTok.
    addTikTokEvent('CompleteRegistrationGoogle');

    // UET.
    addUETEvent('event', 'Free_google', { event_category: 'Signup' });
    addUETEvent('event', 'for_audience', { event_category: 'Signup' });

    // Clarity.
    addClarityEvent('signup_completed');
  } catch (e) {
    console.warn('Error tracking registration', e);
  }
};

const trackCreatorPurchase = (length: 'Annual' | 'Monthly') => {
  if (length === 'Annual') {
    // GA.
    addGAEvent('subscription-annual');

    // Ads.
    addAdEvent(AdEvents.Creator.Annual);
  } else if (length === 'Monthly') {
    // Ads.
    addAdEvent(AdEvents.Creator.Monthly);

    // GA.
    // Typo is intentional to match GA event.
    addGAEvent('subscription-monhtly');
  }

  // Meta.
  addFBEvent(`Subscription-${length}`);
  // Also track the aggregated paid subscription event
  addFBEvent('Subscription-Paid-Conversion');

  // TikTok.
  addTikTokEvent(`Premium_${length}`);

  // UET.
  addUETEvent('event', length, { event_category: 'Subscription' });
};

const trackProPurchase = (length: 'Annual' | 'Monthly') => {
  if (length === 'Annual') {
    // GA.
    addGAEvent('business-subscription-annual');

    // Ads.
    addAdEvent(AdEvents.Pro.Annual);
  } else if (length === 'Monthly') {
    // GA.
    addGAEvent('business-subscription-monthly');

    // Ads.
    addAdEvent(AdEvents.Pro.Monthly);
  }

  // Meta.
  addFBEvent(`Subscription-${length}-Business`);
  // Also track the aggregated paid subscription event
  addFBEvent('Subscription-Paid-Conversion');

  // TikTok.
  addTikTokEvent(`Business_${length}`);

  // UET.
  addUETEvent('event', `Business ${length}`, {
    event_category: 'Subscription',
  });
};

const trackEssentialsPurchase = (length: 'Annual' | 'Monthly') => {
  if (length === 'Annual') {
    // GA.
    addGAEvent('essentials-subscription-annual');

    // Ads.
    addAdEvent(AdEvents.Essentials.Annual);
  } else if (length === 'Monthly') {
    // GA.
    addGAEvent('essentials-subscription-monthly');

    // Ads.
    addAdEvent(AdEvents.Essentials.Monthly);
  }

  // Meta.
  addFBEvent(`Subscription-${length}-Essentials`);
  // Also track the aggregated paid subscription event
  addFBEvent('Subscription-Paid-Conversion');

  // TikTok.
  addTikTokEvent(`Essentials_${length}`);

  // UET.
  addUETEvent('event', `Essentials ${length}`, {
    event_category: 'Subscription',
  });
};

// The "Business"-named events above belong to Pro (its pre-2025 name); the 2026
// Business tier uses "company" names instead (DEV-6540). Meta leads with
// `subscription-`, everywhere else trails it — the two orders are deliberate.
const trackBusinessPurchase = (length: 'Annual' | 'Monthly') => {
  const period = length.toLowerCase();

  // GA.
  addGAEvent(`company-subscription-${period}`);

  // Ads.
  addAdEvent(AdEvents.Business[length]);

  // Meta.
  addFBEvent(`subscription-company-${period}`);
  // Also track the aggregated paid subscription event
  addFBEvent('Subscription-Paid-Conversion');

  // TikTok.
  addTikTokEvent(`BusinessTier_${length}`);

  // UET.
  addUETEvent('event', `company-subscription-${period}`, {
    event_category: 'Subscription',
  });

  // Clarity.
  addClarityEvent(`company-subscription-${period}`);
};

export const trackPurchase = (
  type: Plan['name'],
  length: 'Annual' | 'Monthly'
) => {
  try {
    if (type === 'Creator') {
      trackCreatorPurchase(length);
    } else if (type === 'Pro') {
      trackProPurchase(length);
    } else if (type === 'Essentials') {
      trackEssentialsPurchase(length);
    } else if (type === 'Business') {
      trackBusinessPurchase(length);
    } else {
      Sentry.captureException(
        `Unknown plan type: ${type} - cannot track purchase`
      );
    }

    // Clarity - track all subscription purchases
    addClarityEvent('subscription_purchased');
  } catch (e) {
    console.warn('Error tracking purchase', e);

    Sentry.captureException(e);
  }
};

export const trackDownload = () => {
  try {
    // GA.
    addGAEvent('click-download');

    // Ads.
    addAdEvent('48KACPWHmPABENvO5dUB');

    // Meta.
    addFBEvent('Click-download-button');

    // TikTok.
    addTikTokEvent('Download');

    // UET.
    addUETEvent('event', 'Download_button', { event_category: 'Click' });
    addUETEvent('event', 'for_audience', {
      event_category: 'microconversions',
    });
  } catch (e) {
    console.warn('Error tracking download', e);
  }
};

export const trackSFXDownload = () => {
  try {
    // GA.
    addGAEvent('downloaded-Sfx');

    // Ads.
    addAdEvent('48KACPWHmPABENvO5dUB');

    // Meta.
    addFBEvent('Download-sfx');

    // TikTok.
    addTikTokEvent('DownloadSfx');
  } catch (e) {
    console.warn('Error tracking SFX download', e);
  }
};

export const trackMGDownload = () => {
  try {
    // GA.
    addGAEvent('download-mg');

    addAdEvent('wxsxCJyozpYaENvO5dUB');

    // Meta.
    addFBEvent('Download-mg');

    // TikTok.
    addTikTokEvent('DownloadMG');

    // UET.
    addUETEvent('event', 'DownloadMG', { event_category: 'Click' });
    addUETEvent('event', 'for_audience', {
      event_category: 'microconversions',
    });
  } catch (e) {
    console.warn('Error tracking download', e);
  }
};

export const trackSearchEngagement = (term: string) => {
  try {
    // GA.
    addGAEvent('engagement-search', { value: term });

    // Ads.
    addAdEvent('U8HjCKKKhfABENvO5dUB');

    // Meta.
    addFBEvent('Used-search');

    // TikTok.
    addTikTokEvent('Search');

    // UET.
    addUETEvent('event', 'search', { event_category: 'Used' });
    addUETEvent('event', 'for_audience', {
      event_category: 'microconversions',
    });
  } catch (e) {
    console.warn('Error tracking search engagement', e);
  }
};

export const trackBoardVisibilityChange = (isPublic: boolean) => {
  try {
    addGAEvent(isPublic ? 'board-set-public' : 'board-set-private');
    addClarityEvent(isPublic ? 'board_set_public' : 'board_set_private');
  } catch (e) {
    console.warn('Error tracking board visibility change', e);
  }
};

export const trackSharedBoardPrivateAccess = () => {
  try {
    addGAEvent('shared-board-private-access');
    addClarityEvent('shared_board_private_access');
  } catch (e) {
    console.warn('Error tracking shared board private access', e);
  }
};

export const trackSharedBoardExternalView = () => {
  try {
    addGAEvent('shared-board-external-view');
    addClarityEvent('shared_board_external_view');
  } catch (e) {
    console.warn('Error tracking shared board external view', e);
  }
};

export const trackSharedBoardSignupConversion = () => {
  try {
    addGAEvent('shared-board-signup-conversion');
    addClarityEvent('shared_board_signup_conversion');
  } catch (e) {
    console.warn('Error tracking shared board signup conversion', e);
  }
};

export const trackSharedBoardSubscriptionConversion = () => {
  try {
    addGAEvent('shared-board-subscription-conversion');
    addClarityEvent('shared_board_subscription_conversion');
  } catch (e) {
    console.warn('Error tracking shared board subscription conversion', e);
  }
};
